/**
 * Danh mục tham chiếu cho nhập Excel (04/11/2026) — một định nghĩa dùng chung cho mọi màn danh mục.
 * Khớp theo mã / tên; tạo tự động / mở form chỉ khi người dùng có quyền thêm ở bảng phân quyền.
 */
import { useMemo } from 'react';
import { trpcClient } from '@/lib/trpc';
import { useRoleAccess } from '@/hooks/useRoleAccess';
import type { ImportRefDef, ImportRefOption } from '@/lib/import-kit';
import { WAREHOUSE_DOMAIN_LIST } from '@apv-erp/status';

const items = (res: any): any[] => (Array.isArray(res) ? res : res?.items || res?.data || []);
const opt = (value: unknown, label: unknown, ...keys: unknown[]): ImportRefOption => ({
  value: String(value),
  label: String(label ?? value),
  keys: [value, label, ...keys].filter((k) => k !== null && k !== undefined && String(k).trim() !== '').map(String),
});

export function useImportRefs() {
  const { hasPermission } = useRoleAccess();
  return useMemo(() => {
    const canConfig = hasPermission('configs', 'create');

    /** Mục trong danh mục Cấu hình (giá trị ghi = tên mục) */
    const configItems = (
      categoryCode: string,
      label: string,
      opts: { creatable?: boolean; createHint?: string; valueField?: 'code' | 'name' } = {},
    ): ImportRefDef => ({
      label,
      canCreate: (opts.creatable ?? true) && canConfig,
      createHint: opts.createHint || (canConfig ? undefined : `Chưa có quyền thêm mục ở Cấu hình › ${label}`),
      load: async () => {
        const cat: any = await trpcClient.config.getCategoryWithItems.query({ code: categoryCode });
        return (cat?.items || [])
          .filter((i: any) => i.active !== false && !i.isDeleted)
          .map((i: any) =>
            opt(opts.valueField === 'code' ? i.code || i.name : i.name, i.name, i.code, ...(Array.isArray(i.data?.aliases) ? i.data.aliases : [])),
          );
      },
      create: async (raw) => {
        const item: any = await trpcClient.config.addConfigItem.mutate({ categoryCode, name: raw });
        return opts.valueField === 'code' ? String(item?.code || item?.name || raw) : raw;
      },
    });

    const vendor: ImportRefDef = {
      label: 'Nhà cung cấp',
      canCreate: hasPermission('vendors', 'create'),
      quickAddType: 'vendor',
      formDefaults: (raw) => ({ code: raw, name: raw }),
      load: async () =>
        items(await trpcClient.vendors.list.query({ limit: 10000 } as any)).map((v: any) =>
          opt(v.id, v.code, v.name, v.shortName, v.fullName),
        ),
      create: async (raw) => String(((await trpcClient.vendors.create.mutate({ code: raw, name: raw } as any)) as any).id),
    };

    const customer: ImportRefDef = {
      label: 'Khách hàng',
      canCreate: hasPermission('customers', 'create'),
      quickAddType: 'customer',
      formDefaults: (raw) => ({ code: raw, name: raw }),
      load: async () =>
        items(await trpcClient.customers.list.query({ limit: 10000 } as any)).map((c: any) =>
          opt(c.id, c.code, c.name, c.shortName, c.fullName),
        ),
      create: async (raw) => String(((await trpcClient.customers.create.mutate({ code: raw, name: raw } as any)) as any).id),
    };

    const materialType: ImportRefDef = {
      label: 'Loại vật tư',
      canCreate: hasPermission('materials', 'create') || canConfig,
      quickAddType: 'materialType',
      formDefaults: (raw) => ({ code: raw, label: raw }),
      load: async () =>
        items(await trpcClient.config.getMaterialTypes.query()).map((t: any) => opt(t.id, t.label || t.code, t.code)),
      create: async (raw) => String(((await trpcClient.config.addMaterialType.mutate({ code: raw, label: raw })) as any).id),
    };

    const unit = configItems('units', 'Đơn vị tính', {
      createHint: canConfig ? 'ĐVT mới thêm vào Cấu hình › Đơn vị tính' : 'ĐVT mới do Admin thêm ở Cấu hình › Đơn vị tính',
    });

    /** Phòng ban (bảng departments): giá trị = mã phòng ban (nhân sự) hoặc id (thiết bị) */
    const department = (valueField: 'code' | 'id'): ImportRefDef => ({
      label: 'Phòng ban',
      canCreate: false,
      createHint: 'Phòng ban mới thêm ở Hệ thống › Phòng ban',
      load: async () =>
        items(await trpcClient.users.getDepartments.query()).map((d: any) => opt(d[valueField], d.name, d.code)),
    });

    const role: ImportRefDef = {
      label: 'Vai trò',
      canCreate: false,
      createHint: 'Vai trò mới tạo ở Phân quyền (cần tích quyền trước khi dùng)',
      load: async () => items(await trpcClient.roles.list.query()).map((r: any) => opt(r.roleId, r.name)),
    };

    const machineType: ImportRefDef = {
      ...configItems('machine_type', 'Loại máy', { creatable: false, createHint: 'Loại máy là danh sách cố định' }),
      load: async () => {
        const cat: any = await trpcClient.config.getCategoryWithItems.query({ code: 'machine_type' });
        return (cat?.items || []).filter((i: any) => i.code).map((i: any) => opt(i.code, i.name));
      },
    };

    /** Danh sách cố định (mã ↔ tên hiển thị), không tạo thêm từ file */
    const fixed = (label: string, opts: ImportRefOption[], hint: string): ImportRefDef => ({
      label,
      canCreate: false,
      createHint: hint,
      load: async () => opts,
    });

    // Loại kho: mã đầu tiên của nhóm kho (VD Kho Vật tư → MATERIAL); khớp cả tên, slug và mọi mã của nhóm
    const warehouseType = fixed(
      'Loại kho',
      WAREHOUSE_DOMAIN_LIST.map((d: any) => opt(d.warehouseTypes[0], d.label, d.slug, d.domain, ...d.warehouseTypes)),
      'Loại kho là danh sách cố định',
    );
    const activeStatus = (activeLabel: string, inactiveLabel: string) =>
      fixed('Trạng thái', [opt('ACTIVE', activeLabel, 'Hoạt động', 'Đang dùng'), opt('INACTIVE', inactiveLabel, 'Ngưng', 'Tạm khóa', 'Ngưng dùng')], 'Chỉ nhận Hoạt động / Ngưng');

    return {
      warehouseType,
      warehouseStatus: activeStatus('Hoạt động', 'Tạm khóa'),
      toolingStatus: activeStatus('Đang dùng', 'Ngưng dùng'),
      materialGroup: configItems('material_groups', 'Nhóm vật tư (loại)'),
      toolingCategory: configItems('TOOLING_CATEGORY', 'Phân loại CCDC', { valueField: 'code' }),
      toolingUnit: configItems('UNIT_TOOLING', 'ĐVT CCDC', { valueField: 'code' }),
      vendor,
      customer,
      materialType,
      unit,
      department,
      role,
      machineType,
      materialSubtype: configItems('material_subtypes', 'Nhóm vật tư'),
      vendorGroup: configItems('vendor_groups', 'Nhóm NCC', { valueField: 'code' }),
      vendorType: configItems('vendor_types', 'Loại NCC', { valueField: 'code' }),
      customerGroup: configItems('customer_groups', 'Nhóm khách hàng'),
    };
  }, [hasPermission]);
}
